import type { EventOwnerView } from "@shared/schemas/event.schemas";
import { dal } from "@frontend/shared/data/frontend-dal";
import type { FrontendDAL } from "@frontend/shared/data/frontend-dal";
import { useGatedAction } from "../gated-action";
import { gateEnabled } from "../gate-enabled";
import type { EventView } from "./use-event-actions";

/**
 * Narrows a resolved event view to the owner tier. The `event:owner` role is only
 * ever stamped on the owner-tier view returned by the backend, so its presence is
 * a sound discriminant (no type assertion needed).
 */
function isOwnerEventView(
  event: EventView | undefined,
): event is EventOwnerView {
  return event?.$roles.includes("event:owner") ?? false;
}

export interface EventOwnerActionsContext {
  readonly event: EventView | undefined;
  readonly isLoading: boolean;
  readonly error: Error | null;
}

export interface EventOwnerBundle {
  readonly updateEvent: ReturnType<FrontendDAL["events"]["useUpdateEventHost"]>;
  readonly stageEventBrandingUpload: ReturnType<
    FrontendDAL["events"]["useStageEventBrandingUpload"]
  >;
  readonly removeParticipant: ReturnType<
    FrontendDAL["participants"]["useRemoveParticipant"]
  >;
  readonly setParticipantVip: ReturnType<
    FrontendDAL["participants"]["useSetParticipantVip"]
  >;
  readonly importParticipants: ReturnType<
    FrontendDAL["imports"]["useImportJson"]
  >;
  readonly classifyParticipants: ReturnType<
    FrontendDAL["imports"]["useClassifyJson"]
  >;
  readonly createOpenInvitation: ReturnType<
    FrontendDAL["openInvitations"]["useCreate"]
  >;
  readonly disableOpenInvitation: ReturnType<
    FrontendDAL["openInvitations"]["useDisable"]
  >;
  readonly editDefaultInvitationPhrase: ReturnType<
    FrontendDAL["openInvitations"]["useEditDefaultPhrase"]
  >;
  readonly createOpenInvitationMagicLinks: ReturnType<
    FrontendDAL["openInvitations"]["useCreateMagicLinks"]
  >;
  readonly updateFeatureConfig: ReturnType<
    FrontendDAL["featureConfig"]["useUpdateFeatureConfig"]
  >;
  readonly updateFeatureConfigs: ReturnType<
    FrontendDAL["featureConfig"]["useUpdateFeatureConfigs"]
  >;
  readonly resetFeatureConfig: ReturnType<
    FrontendDAL["featureConfig"]["useResetFeatureConfig"]
  >;
  readonly saveConnectionOverrides: ReturnType<
    FrontendDAL["connections"]["useSaveConnectionOverrides"]
  >;
  readonly revertConnectionOverrides: ReturnType<
    FrontendDAL["connections"]["useRevertConnectionOverrides"]
  >;
  readonly addEventHost: ReturnType<
    FrontendDAL["eventHosts"]["useAddEventHost"]
  >;
  readonly removeEventHost: ReturnType<
    FrontendDAL["eventHosts"]["useRemoveEventHost"]
  >;
}

export interface EventOwnerQueries {
  readonly useGetDirectoryEntry: FrontendDAL["directory"]["useGetDirectoryEntry"];
  readonly useGetParticipants: FrontendDAL["participants"]["useGetParticipants"];
  readonly fetchParticipants: FrontendDAL["participants"]["fetchParticipants"];
  readonly useGetEventDashboard: FrontendDAL["dashboard"]["useGetEventDashboard"];
  readonly useGetConnectionsView: FrontendDAL["connections"]["useGetConnectionsView"];
  readonly useGetEventConnectionRequests: FrontendDAL["connectionRequests"]["useGetEventConnectionRequests"];
  readonly useGetParticipantRoster: FrontendDAL["participantRoster"]["useGetParticipantRoster"];
  readonly useGetEventUsers: FrontendDAL["eventUsers"]["useGetEventUsers"];
  readonly fetchEventUsers: FrontendDAL["eventUsers"]["fetchEventUsers"];
  readonly useGetActivityLog: FrontendDAL["participants"]["useGetActivityLog"];
  readonly useGetParticipantMilestones: FrontendDAL["participants"]["useGetParticipantMilestones"];
  readonly useGetByEventIdPublic: FrontendDAL["openInvitations"]["useGetByEventIdPublic"];
  readonly useGetByEventIdPublicResolved: FrontendDAL["openInvitations"]["useGetByEventIdPublicResolved"];
  readonly useGetEventHosts: FrontendDAL["eventHosts"]["useGetEventHosts"];
}

export interface EventOwnerActions extends EventOwnerQueries {
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly owner: EventOwnerBundle | null;
  readonly ownerEvent: EventOwnerView | null;
}

/**
 * Event-owner actions — mirrors the backend `EventOwnerActions`. Always returns a
 * non-null result object; the inner `owner` mutation bundle is `null` while the
 * parent `useEventActions` is loading or when the caller is not an owner
 * (`event.$roles` lacks `event:owner`) — a non-null `owner` IS the owner check.
 * Mutations live in the `owner` bundle (inert until `.mutate()`); query sub-hooks
 * are siblings the consumer opts into, each additionally `enabled`-gated by owner
 * where the DAL hook supports it (`useGetActivityLog` and the open-invitations
 * reads take no `options`, so they rely on the bundle/consumer for gating).
 * Custom-field-definition mutations are intentionally omitted — no frontend
 * consumer (custom fields ride on the event admin/internal view).
 */
export function useEventOwnerActions(
  context: EventOwnerActionsContext,
): EventOwnerActions {
  const { isLoading, error, subject, bundle, ...queries } = useGatedAction({
    useDeps: () => ({
      updateEvent: dal().events.useUpdateEventHost(),
      stageEventBrandingUpload: dal().events.useStageEventBrandingUpload(),
      removeParticipant: dal().participants.useRemoveParticipant(),
      setParticipantVip: dal().participants.useSetParticipantVip(),
      importParticipants: dal().imports.useImportJson(),
      classifyParticipants: dal().imports.useClassifyJson(),
      createOpenInvitation: dal().openInvitations.useCreate(),
      disableOpenInvitation: dal().openInvitations.useDisable(),
      editDefaultInvitationPhrase: dal().openInvitations.useEditDefaultPhrase(),
      createOpenInvitationMagicLinks:
        dal().openInvitations.useCreateMagicLinks(),
      updateFeatureConfig: dal().featureConfig.useUpdateFeatureConfig(),
      updateFeatureConfigs: dal().featureConfig.useUpdateFeatureConfigs(),
      resetFeatureConfig: dal().featureConfig.useResetFeatureConfig(),
      saveConnectionOverrides: dal().connections.useSaveConnectionOverrides(),
      revertConnectionOverrides:
        dal().connections.useRevertConnectionOverrides(),
      addEventHost: dal().eventHosts.useAddEventHost(),
      removeEventHost: dal().eventHosts.useRemoveEventHost(),
    }),
    isLoading: () => context.isLoading,
    error: () => context.error,
    authorize: () => (isOwnerEventView(context.event) ? context.event : null),
    bundle: (d) => ({
      updateEvent: d.updateEvent,
      stageEventBrandingUpload: d.stageEventBrandingUpload,
      removeParticipant: d.removeParticipant,
      setParticipantVip: d.setParticipantVip,
      importParticipants: d.importParticipants,
      classifyParticipants: d.classifyParticipants,
      createOpenInvitation: d.createOpenInvitation,
      disableOpenInvitation: d.disableOpenInvitation,
      editDefaultInvitationPhrase: d.editDefaultInvitationPhrase,
      createOpenInvitationMagicLinks: d.createOpenInvitationMagicLinks,
      updateFeatureConfig: d.updateFeatureConfig,
      updateFeatureConfigs: d.updateFeatureConfigs,
      resetFeatureConfig: d.resetFeatureConfig,
      saveConnectionOverrides: d.saveConnectionOverrides,
      revertConnectionOverrides: d.revertConnectionOverrides,
      addEventHost: d.addEventHost,
      removeEventHost: d.removeEventHost,
    }),
    extra: (_d, { isAuthorized: isOwner }): EventOwnerQueries => ({
      useGetDirectoryEntry: (options) =>
        dal().directory.useGetDirectoryEntry({
          ...options,
          enabled: gateEnabled(isOwner, options.enabled),
        }),
      useGetParticipants: (params, options) =>
        dal().participants.useGetParticipants(params, {
          ...options,
          enabled: gateEnabled(isOwner, options?.enabled),
        }),
      useGetEventDashboard: (eventIdPublic, options) =>
        dal().dashboard.useGetEventDashboard(eventIdPublic, {
          ...options,
          enabled: gateEnabled(isOwner, options?.enabled),
        }),
      useGetConnectionsView: (eventIdPublic, options) =>
        dal().connections.useGetConnectionsView(eventIdPublic, {
          ...options,
          enabled: gateEnabled(isOwner, options?.enabled),
        }),
      useGetEventConnectionRequests: (params, options) =>
        dal().connectionRequests.useGetEventConnectionRequests(params, {
          ...options,
          enabled: gateEnabled(isOwner, options?.enabled),
        }),
      useGetParticipantRoster: (eventIdPublic, options) =>
        dal().participantRoster.useGetParticipantRoster(eventIdPublic, {
          ...options,
          enabled: gateEnabled(isOwner, options?.enabled),
        }),
      useGetEventHosts: (eventIdPublic, options) =>
        dal().eventHosts.useGetEventHosts(eventIdPublic, {
          ...options,
          enabled: gateEnabled(isOwner, options?.enabled),
        }),
      useGetEventUsers: (eventIdPublic, options) =>
        dal().eventUsers.useGetEventUsers(eventIdPublic, {
          ...options,
          enabled: gateEnabled(isOwner, options?.enabled),
        }),
      useGetParticipantMilestones: (
        eventIdPublic,
        participantIdPublic,
        options,
      ) =>
        dal().participants.useGetParticipantMilestones(
          eventIdPublic,
          participantIdPublic,
          { ...options, enabled: gateEnabled(isOwner, options?.enabled) },
        ),
      // Ungated: imperative fetches have no `enabled` to gate; the server's
      // owner check still applies.
      fetchParticipants: (params) =>
        dal().participants.fetchParticipants(params),
      fetchEventUsers: (eventIdPublic) =>
        dal().eventUsers.fetchEventUsers(eventIdPublic),
      // Ungated (their DAL hooks take no `options`); the null `owner` bundle +
      // the consumer opting in gate access.
      useGetActivityLog: (eventIdPublic, participantIdPublic, pagination) =>
        dal().participants.useGetActivityLog(
          eventIdPublic,
          participantIdPublic,
          pagination,
        ),
      useGetByEventIdPublic: (eventIdPublic) =>
        dal().openInvitations.useGetByEventIdPublic(eventIdPublic),
      useGetByEventIdPublicResolved: (eventIdPublic) =>
        dal().openInvitations.useGetByEventIdPublicResolved(eventIdPublic),
    }),
  });

  return { isLoading, error, owner: bundle, ownerEvent: subject, ...queries };
}
